import { Modal, Select, Spin, Tooltip, Typography } from "antd";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import useApi from "../../utils/useApi";

const { Text } = Typography;

const HOURS = Array.from({ length: 24 }, (_, i) => i);

function cellColor(uptime) {
  if (uptime == null) return "#f0f0f0";
  if (uptime >= 99) return "#52c41a";
  if (uptime >= 80) return "#faad14";
  return "#ff4d4f";
}

export default function CameraHistoryModal({ camera, onClose }) {
  const [days, setDays] = useState(30);
  const { getCameraMonitoringHistory } = useApi();

  const { data, isLoading } = useQuery({
    queryKey: ["camera-history", camera.id, days],
    queryFn: () =>
      getCameraMonitoringHistory(camera.id, days).then((r) => r.data),
    staleTime: 60_000,
  });

  const history = data?.history ?? [];

  const lookup = {};
  history.forEach(({ date, hour, uptime_pct, total_checks }) => {
    if (!lookup[date]) lookup[date] = {};
    lookup[date][hour] = { uptime_pct, total_checks };
  });

  const dates = [...new Set(history.map((r) => r.date))].sort().reverse();

  return (
    <Modal
      open
      title={`Histórico — ${camera.name} (${camera.branch_name})`}
      onCancel={onClose}
      footer={null}
      width={820}>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}>
        <Text>Período:</Text>
        <Select
          value={days}
          onChange={setDays}
          options={[
            { value: 7, label: "7 dias" },
            { value: 30, label: "30 dias" },
            { value: 90, label: "90 dias" },
          ]}
          style={{ width: 120 }}
          size="small"
        />
      </div>

      {isLoading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : dates.length === 0 ? (
        <Text type="secondary">Sem dados para o período selecionado.</Text>
      ) : (
        <div style={{ overflowX: "auto" }}>
          {/* Hour axis labels */}
          <div style={{ display: "flex", marginLeft: 68, marginBottom: 4 }}>
            {HOURS.map((h) => (
              <div
                key={h}
                style={{
                  width: 26,
                  textAlign: "center",
                  fontSize: 10,
                  color: "#8c8c8c",
                  visibility: h % 6 === 0 ? "visible" : "hidden",
                }}>
                {String(h).padStart(2, "0")}h
              </div>
            ))}
          </div>

          {/* One row per date */}
          {dates.map((date) => (
            <div
              key={date}
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 3,
              }}>
              <div
                style={{
                  width: 68,
                  fontSize: 11,
                  color: "#595959",
                  flexShrink: 0,
                }}>
                {date.slice(8) + "/" + date.slice(5, 7)}
              </div>
              {HOURS.map((h) => {
                const cell = lookup[date]?.[h];
                const uptime = cell?.uptime_pct;
                const label =
                  uptime != null
                    ? `${date} ${String(h).padStart(2, "0")}h — ${uptime.toFixed(1)}% uptime (${cell.total_checks} checks)`
                    : `${date} ${String(h).padStart(2, "0")}h — fora do horário`;
                return (
                  <Tooltip key={h} title={label} placement="top">
                    <div
                      style={{
                        width: 24,
                        height: 18,
                        backgroundColor: cellColor(uptime),
                        marginRight: 2,
                        borderRadius: 2,
                        cursor: cell ? "default" : "default",
                      }}
                    />
                  </Tooltip>
                );
              })}
            </div>
          ))}

          {/* Legend */}
          <div
            style={{
              display: "flex",
              gap: 16,
              marginTop: 14,
              fontSize: 11,
              color: "#595959",
              flexWrap: "wrap",
            }}>
            {[
              { color: "#52c41a", label: "≥ 99%" },
              { color: "#faad14", label: "80–99%" },
              { color: "#ff4d4f", label: "< 80%" },
              {
                color: "#f0f0f0",
                label: "Fora do horário",
                border: "1px solid #d9d9d9",
              },
            ].map(({ color, label, border }) => (
              <span
                key={label}
                style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span
                  style={{
                    display: "inline-block",
                    width: 12,
                    height: 12,
                    background: color,
                    border,
                    borderRadius: 2,
                  }}
                />
                {label}
              </span>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}
